import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuditLog } from "@/types/audit";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getActionLabel, getEntityTypeLabel } from "@/lib/audit/action-labels";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AuditDetailModalProps {
  log: AuditLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuditDetailModal({ log, open, onOpenChange }: AuditDetailModalProps) {
  if (!log) return null;

  const renderJsonDiff = (oldValues: any, newValues: any) => {
    if (!oldValues && !newValues) return <p className="text-muted-foreground">Nenhuma alteração de valores</p>;

    return (
      <div className="grid grid-cols-2 gap-4">
        {oldValues && (
          <div>
            <h4 className="font-semibold mb-2 text-destructive">Valores Anteriores</h4>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
              {JSON.stringify(oldValues, null, 2)}
            </pre>
          </div>
        )}
        {newValues && (
          <div>
            <h4 className="font-semibold mb-2 text-green-600">Novos Valores</h4>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
              {JSON.stringify(newValues, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Detalhes da Auditoria
            <Badge variant="outline">{getActionLabel(log.action)}</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Data/Hora</p>
                <p className="text-sm">
                  {format(new Date(log.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", { locale: ptBR })}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tipo de Entidade</p>
                <p className="text-sm">{getEntityTypeLabel(log.entity_type)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID do Usuário</p>
                <p className="text-sm font-mono text-xs">{log.actor_user_id || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID da Entidade</p>
                <p className="text-sm font-mono text-xs">{log.entity_id || '-'}</p>
              </div>
              {log.company_id && (
                <div className="col-span-2">
                  <p className="text-sm font-medium text-muted-foreground">ID da Empresa</p>
                  <p className="text-sm font-mono text-xs">{log.company_id}</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Alterações</h3>
              {renderJsonDiff(log.old_values, log.new_values)}
            </div>

            {log.meta && Object.keys(log.meta).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Metadados</h3>
                <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
                  {JSON.stringify(log.meta, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
